import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react';
import { ApiFailure, newIdempotencyKey } from './client.js';

/** Anything thrown out of a screen becomes an ApiFailure so one renderer covers it. */
export function asFailure(error: unknown): ApiFailure {
  if (error instanceof ApiFailure) return error;
  return new ApiFailure(
    0,
    'CLIENT_ERROR',
    error instanceof Error ? error.message : String(error),
    null,
    null,
  );
}

export interface Loaded<T> {
  data: T | null;
  error: ApiFailure | null;
  loading: boolean;
  reload: () => void;
}

export interface UseApiOptions {
  /** Poll while the answer is non-terminal. Undefined = load once. */
  pollMs?: number;
  skip?: boolean;
}

/**
 * A read. `data` and `error` are never both set: a reload that fails replaces the
 * data with the failure rather than showing stale rows under a fresh error.
 */
export function useApi<T>(
  load: () => Promise<T>,
  deps: DependencyList,
  options: UseApiOptions = {},
): Loaded<T> {
  const { pollMs, skip = false } = options;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiFailure | null>(null);
  const [loading, setLoading] = useState(!skip);
  const [nonce, setNonce] = useState(0);

  // The latest closure, so deps stay explicit instead of re-firing on identity.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    loadRef.current().then(
      (result) => {
        if (!live) return;
        setData(result);
        setError(null);
        setLoading(false);
      },
      (failure: unknown) => {
        if (!live) return;
        setData(null);
        setError(asFailure(failure));
        setLoading(false);
      },
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, skip]);

  useEffect(() => {
    if (pollMs === undefined) return;
    // Polls only while the tab is VISIBLE. A screen waiting on an external
    // flow sits hidden behind that provider's tab for its whole duration —
    // polling it there is pure request spam the user once screenshotted (33
    // identical fetches while a checkout tab was in front).
    // Becoming visible refetches IMMEDIATELY, so returning to the tab shows
    // the webhook's answer now, not up to a poll-interval later.
    let timer: number | undefined;
    const stop = () => {
      if (timer !== undefined) window.clearInterval(timer);
      timer = undefined;
    };
    const start = () => {
      timer ??= window.setInterval(() => setNonce((value) => value + 1), pollMs);
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        setNonce((value) => value + 1);
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pollMs]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  return { data, error, loading, reload };
}

export interface Action<A extends unknown[], R> {
  run: (...args: A) => void;
  pending: boolean;
  error: ApiFailure | null;
  result: R | null;
  reset: () => void;
}

/** A write. Never fires twice concurrently — a double-tap is dropped, not queued. */
export function useAction<A extends unknown[], R>(
  perform: (...args: A) => Promise<R>,
  onSuccess?: (result: R) => void,
): Action<A, R> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiFailure | null>(null);
  const [result, setResult] = useState<R | null>(null);
  const performRef = useRef(perform);
  performRef.current = perform;
  const successRef = useRef(onSuccess);
  successRef.current = onSuccess;
  const inFlight = useRef(false);

  const run = useCallback((...args: A) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError(null);
    performRef.current(...args).then(
      (value) => {
        inFlight.current = false;
        setPending(false);
        setResult(value);
        successRef.current?.(value);
      },
      (failure: unknown) => {
        inFlight.current = false;
        setPending(false);
        setError(asFailure(failure));
      },
    );
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setResult(null);
  }, []);

  return { run, pending, error, result, reset };
}

/**
 * One idempotency key per user INTENT.
 *
 * The key is stable while the user retries the same submission — that is what
 * makes a retry after a lost response a replay instead of a second charge. Call
 * `next()` only once the intent is finished, or when the user edits the input and
 * is deliberately asking for a different outcome.
 */
export function useIntentKey(): { key: string; next: () => void } {
  const [key, setKey] = useState(newIdempotencyKey);
  const next = useCallback(() => setKey(newIdempotencyKey()), []);
  return { key, next };
}
